import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from "react-redux";
import { setAlert } from "../store/slices/alertSlice";
import {
  departmentsUser,
  searchUsers
} from "../services/api";

import "../styles/incident.css";
import UserPickerOption from "./UserPickerOption";
import DismissibleWorkflowAlert from "./DismissibleWorkflowAlert";
import {
  formatUserOptionLabel,
  getUserDisplayName,
} from "../utils/userDisplay";

const HOD_ROLE_NAME = "Supervisor/HOD";

const getUserRoleName = (user) => user?.role?.roleName || "";

const isHodRoleUser = (user) => getUserRoleName(user) === HOD_ROLE_NAME;

const ImmediateAction = ({ data, onChange, disabled = false }) => {

  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [showUsers, setShowUsers] = useState(false);

  const dispatch = useDispatch();
  const dropdownRef = useRef(null);

  /* ---------------- FETCH DEPARTMENTS ---------------- */
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const departmentsResponse = await departmentsUser();
        setDepartments(departmentsResponse.data);
      } catch (error) {
        dispatch(setAlert({ message: 'Error fetching data', type: 'danger' }));
      }
    };
    fetchInitialData();
  }, [dispatch]);

  /* ---------------- HANDLE INPUT ---------------- */
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    onChange({
      ...data,
      [name]: value,
    });
  };

  /* ---------------- USER SEARCH ---------------- */
  const handleUserSearch = async (e) => {

    const searchText = e.target.value;
    setUserSearch(searchText);

    if (!searchText) {
      setUsers([]);
      setShowUsers(false);
      return;
    }

    try {
      const response = await searchUsers(searchText);
      setUsers(response.data.users);
      setShowUsers(true);
    } catch (error) {
      dispatch(setAlert({ message: "Error loading users", type: "danger" }));
    }
  };

  /* ---------------- SELECT USER ---------------- */
  const selectUser = (user) => {

    // const selectedUsers = data.userIds || [];
    const selectedUsers = data.userObjects || []; // 🔥 keep full objects for UI

    const exists = selectedUsers.find(u => u._id === user._id);

    let updatedUsers;

    if (exists) {
      updatedUsers = selectedUsers.filter(u => u._id !== user._id);
    } else {
      updatedUsers = [...selectedUsers, user];
    }

    // ✅ IMPORTANT FIX: send ONLY IDs to backend
    onChange({
      ...data,
      userIds: updatedUsers.map(u => u._id), // 🔥 FIX
      userObjects: updatedUsers               // 🔥 keep for UI
    });

    setUserSearch("");
    setShowUsers(false);
  };

  /* ---------------- CLOSE DROPDOWN ---------------- */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUsers(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const selectedUsers = data.userObjects || [];
  const selectedHodUsers = selectedUsers.filter(isHodRoleUser);
  const showGuidance = !disabled;

  return (

    <div className="form-container compact">

      {showGuidance && (
      <DismissibleWorkflowAlert variant="info" className="workflow-info-alert mb-3">
        <strong>User assignment is for comments only.</strong>
        <span className="d-block mt-1 small mb-0">
          Users added below will be asked to submit a <em>user comment</em> before the case moves to HOD assignment.
          To send directly to HOD without user comment, leave this field empty and use <em>Complete Investigation</em>.
          HODs are selected later in the <strong>HOD Assignment</strong> section.
        </span>
      </DismissibleWorkflowAlert>
      )}

      {/* Investigation Details */}
      <div className="form-row">
        <div className="form-control full-width">
          <label>Investigation Details <span className="required-mark">*</span></label>
          <textarea
            name="investigationFindings"
            value={data.investigationFindings || ''}
            onChange={handleInputChange}
            className="form-control large-textarea"
            disabled={disabled}
          />
        </div>
      </div>

      {/* Department */}
      <div className="form-row">
        <div className="form-control">
          <label>Department <span className="required-mark">*</span></label>
          <select
            name="department"
            value={data.department || ''}
            onChange={handleInputChange}
            disabled={disabled}
          >
            <option value="">Select Department</option>
            {departments.map((dept) => (
              <option key={dept._id} value={dept._id}>
                {dept.departmentName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Priority */}
      <div className="form-row">
        <div className="form-control">
          <label>Priority <span className="required-mark">*</span></label>
          <select
            name="priority"
            value={data.priority || ''}
            onChange={handleInputChange}
            disabled={disabled}
          >
            <option value="">Select Priority</option>
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>
        </div>
      </div>

      {/* USER MULTI SELECT */}
      <div className="form-row">
        <div className="form-control" style={{ position: "relative" }} ref={dropdownRef}>

          <label>Assign Users for Comment (Optional)</label>
          <span className="form-text text-muted d-block mb-2">
            Search and select users who should provide a comment. This is not for HOD assignment.
          </span>

          <input
            type="text"
            placeholder="Search user to assign for comment..."
            value={userSearch}
            onChange={handleUserSearch}
            className="form-control"
            disabled={disabled}
          />

          {/* Dropdown */}
          {!disabled && showUsers && users.length > 0 && (
            <div className="dropdown-list">
              {users.map((user) => {
                const isSelected = selectedUsers.some((u) => u._id === user._id);
                const isHodUser = isHodRoleUser(user);

                return (
                  <div
                    key={user._id}
                    className={`dropdown-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => selectUser(user)}
                  >
                    <span className="d-flex justify-content-between align-items-start gap-2">
                      <UserPickerOption user={user} />
                      <span className="d-flex align-items-center gap-1">
                        {isHodUser && (
                          <span className="user-role-warning-label">(HOD role)</span>
                        )}
                        {isSelected && <span>✔</span>}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Selected Users */}
          {selectedUsers.length > 0 && (
            <div className="mt-2">
              {selectedUsers.map((u) => {
                const label = formatUserOptionLabel(u);

                return (
                <span
                  key={u._id}
                  className={`badge me-2 mb-1 ${isHodRoleUser(u) ? 'bg-warning text-dark' : 'bg-primary'}`}
                  style={{ cursor: disabled ? "default" : "pointer" }}
                  onClick={() => {
                    if (!disabled) selectUser(u);
                  }}
                  title={label}
                >
                  {label}
                  {isHodRoleUser(u) ? ' (HOD)' : ''} ✕
                </span>
              )})}
            </div>
          )}

          {showGuidance && selectedHodUsers.length > 0 && (
            <DismissibleWorkflowAlert variant="warning" className="workflow-warning-alert mt-3 mb-0">
              <strong>HOD role notice:</strong>
              <span className="d-block mt-1 small mb-0">
                {selectedHodUsers.length === 1
                  ? `${getUserDisplayName(selectedHodUsers[0])} has the HOD role.`
                  : `${selectedHodUsers.map((user) => getUserDisplayName(user)).join(', ')} have the HOD role.`}
                {' '}They are assigned here for <em>user comment</em>, not HOD review.
                To send directly to HOD, remove them from this list and use <strong>HOD Assignment</strong> after investigation.
              </span>
            </DismissibleWorkflowAlert>
          )}

        </div>
      </div>

    </div>
  );
};

export default ImmediateAction;

